/**
 * Keeps the OneTrust consent surface reachable by keyboard. The page can keep
 * its initial focus, but the next Tab/Shift+Tab press moves focus into the
 * banner. Once focus lands inside the consent surface, Tab from the last
 * visible control wraps to the first and Shift+Tab from the first wraps to the
 * last.
 */
export function applyOneTrustFocusTrap() {
  const trapTarget = document.querySelector<HTMLElement>(OT_CONSENT_CONTAINER);

  if (!trapTarget || trapTarget.hasAttribute(TRAP_APPLIED_ATTRIBUTE)) return;

  trapTarget.setAttribute(TRAP_APPLIED_ATTRIBUTE, 'true');

  applyDocumentListeners();

  if (checkIfConsentSurfaceAvailable()) {
    observePreferenceCenterOpenState(trapTarget);
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function applyDocumentListeners() {
  if (consentSurfaceState.hasDocumentListeners) return;

  document.addEventListener('keydown', handleKeyDown, true);
  document.addEventListener('focusin', handleFocusIn);

  consentSurfaceState.hasDocumentListeners = true;
}

function handleKeyDown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    handleEscapeKeyDown(event);

    return;
  }

  if (event.key !== 'Tab') return;

  const trapTarget = getActiveTrapTarget();

  if (!trapTarget) return;

  const focusableElements = getVisibleFocusableElements(trapTarget);

  if (focusableElements.length === 0) return;

  const activeElement = document.activeElement;
  const isValidActiveElement = activeElement instanceof HTMLElement;
  const isInsideTarget = trapTarget.contains(activeElement);
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  if (!firstFocusable || !lastFocusable) return;

  if (!isValidActiveElement || !isInsideTarget) {
    event.preventDefault();

    rememberFocusedElement(activeElement);
    focusEdgeElement({ event, firstFocusable, lastFocusable });
    applyBackgroundContentInertForTrapTarget(trapTarget);

    return;
  }

  if (checkIfPreferenceCenterTrapTarget(trapTarget)) {
    handlePreferenceCenterTabKeyDown({
      activeElement,
      event,
      firstFocusable,
      focusableElements,
      lastFocusable,
    });

    return;
  }

  const nextFocusable = getNextFocusableElement({
    activeElement,
    firstFocusable,
    focusableElements,
    isShiftKeyPressed: event.shiftKey,
    lastFocusable,
  });

  if (!nextFocusable) return;

  event.preventDefault();
  nextFocusable.focus();
}

function handleEscapeKeyDown(event: KeyboardEvent) {
  const trapTarget = document.querySelector<HTMLElement>(OT_CONSENT_CONTAINER);

  if (!trapTarget) return;

  const preferenceCenterCloseButton = getVisiblePreferenceCenterCloseButton();

  if (preferenceCenterCloseButton) {
    event.preventDefault();
    preferenceCenterCloseButton.click();
    restoreWhenConsentSurfaceCloses();

    return;
  }

  // NOTE: A banner without a close control asks for a choice, so Escape must
  //       not dismiss it.
  if (checkIfBannerRequiresChoice()) {
    return;
  }

  const didClose = closeOneTrust();

  if (!didClose) return;

  event.preventDefault();

  restoreWhenConsentSurfaceCloses();
}

function handleFocusIn(event: FocusEvent) {
  const target = event.target;
  const trapTarget = document.querySelector<HTMLElement>(OT_CONSENT_CONTAINER);

  if (!trapTarget || !(target instanceof HTMLElement)) return;

  if (!trapTarget.contains(target)) return;

  observePreferenceCenterOpenState(trapTarget);
  applyBackgroundContentInertForFocusedElement(target);
}

function checkIfBannerRequiresChoice() {
  const banner = document.querySelector<HTMLElement>(BANNER_SELECTOR);
  const closeButton = document.querySelector<HTMLElement>(BANNER_CLOSE_BUTTON_SELECTOR);

  if (!banner || !checkIfVisible(banner)) {
    return false;
  }

  return !closeButton || !checkIfVisible(closeButton);
}

function closeOneTrust() {
  const oneTrust = getOneTrust();

  if (!oneTrust?.Close) {
    return false;
  }

  oneTrust.Close();

  return true;
}

function getActiveTrapTarget() {
  const preferenceCenter = document.querySelector<HTMLElement>(PREFERENCE_CENTER_SELECTOR);

  if (preferenceCenter && checkIfVisible(preferenceCenter)) return preferenceCenter;

  return document.querySelector<HTMLElement>(OT_CONSENT_CONTAINER);
}

function getVisibleFocusableElements(root: HTMLElement) {
  const candidates = [...root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)];
  const visibleCandidates = candidates.filter((candidate) =>
    checkIfElementVisibleWithinRoot({ element: candidate, root }),
  );

  if (checkIfPreferenceCenterTrapTarget(root)) return visibleCandidates;

  return getBannerFocusableElements({ root, visibleCandidates });
}

function getBannerFocusableElements(params: GetBannerFocusableElementsParams) {
  const { root, visibleCandidates } = params;

  const closeButton = root.querySelector<HTMLElement>(BANNER_CLOSE_BUTTON_SELECTOR);

  if (!closeButton || !checkIfVisible(closeButton)) return visibleCandidates;

  return [closeButton, ...visibleCandidates.filter((candidate) => candidate !== closeButton)];
}

function focusEdgeElement(params: FocusEdgeElementParams) {
  const { event, firstFocusable, lastFocusable } = params;

  const element = event.shiftKey ? lastFocusable : firstFocusable;

  element.focus();
}

function handlePreferenceCenterTabKeyDown(params: HandlePreferenceCenterTabKeyDownParams) {
  const { activeElement, event, firstFocusable, focusableElements, lastFocusable } = params;

  const activeElementIndex = focusableElements.indexOf(activeElement);

  if (activeElementIndex === -1) return;

  if (event.shiftKey && activeElement === firstFocusable) {
    event.preventDefault();
    lastFocusable.focus();

    return;
  }

  if (!event.shiftKey && activeElement === lastFocusable) {
    event.preventDefault();
    firstFocusable.focus();
  }
}

function getNextFocusableElement(params: GetNextFocusableElementParams) {
  const { activeElement, firstFocusable, focusableElements, isShiftKeyPressed, lastFocusable } =
    params;

  const activeElementIndex = focusableElements.indexOf(activeElement);

  if (activeElementIndex === -1) {
    return isShiftKeyPressed ? lastFocusable : firstFocusable;
  }

  const nextElementIndex = isShiftKeyPressed ? activeElementIndex - 1 : activeElementIndex + 1;

  if (nextElementIndex < 0) {
    return lastFocusable;
  }

  if (nextElementIndex >= focusableElements.length) {
    return firstFocusable;
  }

  return focusableElements[nextElementIndex];
}

function getVisiblePreferenceCenterCloseButton() {
  const closeButton = document.querySelector<HTMLElement>(`#${PREFERENCE_CENTER_CLOSE_BUTTON_ID}`);

  if (!closeButton) return;

  const preferenceCenter = closeButton.closest<HTMLElement>(PREFERENCE_CENTER_SELECTOR);

  if (!preferenceCenter) return;

  const isPreferenceCenterHidden = checkIfPreferenceCenterHidden({ closeButton, preferenceCenter });

  if (isPreferenceCenterHidden) return;

  return closeButton;
}

function observePreferenceCenterOpenState(trapTarget: HTMLElement) {
  if (trapTarget.hasAttribute(PREFERENCE_CENTER_OBSERVER_APPLIED_ATTRIBUTE)) return;

  const observer = new MutationObserver(() => {
    syncBackgroundContentWithPreferenceCenterOnNextFrame({ observer, trapTarget });
  });

  consentSurfaceState.preferenceCenterObserver?.disconnect();
  consentSurfaceState.preferenceCenterObserver = observer;

  trapTarget.setAttribute(PREFERENCE_CENTER_OBSERVER_APPLIED_ATTRIBUTE, 'true');

  observer.observe(trapTarget, CONSENT_SURFACE_OBSERVER_OPTIONS);
  syncBackgroundContentWithPreferenceCenterOnNextFrame({ observer, trapTarget });
}

function syncBackgroundContentWithPreferenceCenterOnNextFrame(
  params: SyncBackgroundContentWithPreferenceCenterParams,
) {
  if (consentSurfaceState.hasPendingPreferenceCenterSync) return;

  consentSurfaceState.hasPendingPreferenceCenterSync = true;

  requestAnimationFrame(() => {
    syncBackgroundContentWithPreferenceCenter(params);

    requestAnimationFrame(() => {
      syncBackgroundContentWithPreferenceCenter(params);
      consentSurfaceState.hasPendingPreferenceCenterSync = false;
    });
  });
}

function syncBackgroundContentWithPreferenceCenter(
  params: SyncBackgroundContentWithPreferenceCenterParams,
) {
  const { observer, trapTarget } = params;

  if (!trapTarget.isConnected) {
    observer.disconnect();
    consentSurfaceState.preferenceCenterObserver = undefined;
    restoreClosedConsentSurface();

    return;
  }

  if (checkIfPreferenceCenterOpen()) {
    applyBackgroundContentInert();

    return;
  }

  restoreClosedConsentSurface();
}

function restoreWhenConsentSurfaceCloses() {
  consentSurfaceState.restoreObserver?.disconnect();

  if (checkIfPreferenceCenterClosed() || checkIfConsentSurfaceClosed()) {
    restoreClosedConsentSurfaceOnNextFrame();

    return;
  }

  const observer = new MutationObserver(() => {
    restoreIfConsentSurfaceClosed({ observer });
  });

  consentSurfaceState.restoreObserver = observer;

  observeConsentSurfaceClose(observer);
}

function observeConsentSurfaceClose(observer: MutationObserver) {
  const observerTargets = getConsentSurfaceObserverTargets();

  observerTargets.forEach((target) => {
    observer.observe(target, CONSENT_SURFACE_OBSERVER_OPTIONS);
  });
}

function getConsentSurfaceObserverTargets() {
  const consentSurfaceElements = [
    document.querySelector<HTMLElement>(OT_CONSENT_CONTAINER),
    document.querySelector<HTMLElement>(BANNER_SELECTOR),
    document.querySelector<HTMLElement>(PREFERENCE_CENTER_SELECTOR),
  ].filter(checkIfElement);

  const parentElements = consentSurfaceElements.map((element) => element.parentElement);
  const observerTargets = [...consentSurfaceElements, ...parentElements].filter(checkIfElement);

  return [...new Set(observerTargets)];
}

function restoreIfConsentSurfaceClosed(params: RestoreIfConsentSurfaceClosedParams) {
  const { observer } = params;

  if (!checkIfConsentSurfaceClosed()) return;

  observer.disconnect();
  restoreClosedConsentSurfaceOnNextFrame();
}

function checkIfConsentSurfaceClosed() {
  const visibleConsentSurfaces = [
    document.querySelector<HTMLElement>(BANNER_SELECTOR),
    document.querySelector<HTMLElement>(PREFERENCE_CENTER_SELECTOR),
  ].filter(checkIfConsentSurfaceVisible);

  return visibleConsentSurfaces.length === 0;
}

function checkIfConsentSurfaceAvailable() {
  const consentSurface = document.querySelector<HTMLElement>(
    `${BANNER_SELECTOR}, ${PREFERENCE_CENTER_SELECTOR}`,
  );

  return Boolean(consentSurface);
}

function checkIfPreferenceCenterClosed() {
  return !checkIfPreferenceCenterOpen();
}

function checkIfPreferenceCenterOpen() {
  const preferenceCenter = document.querySelector<HTMLElement>(PREFERENCE_CENTER_SELECTOR);

  if (!preferenceCenter) return false;

  return checkIfVisible(preferenceCenter);
}

function checkIfConsentSurfaceVisible(element: HTMLElement | null) {
  if (!element) return false;

  if (checkIfVisible(element)) return true;

  return getVisibleFocusableElements(element).length > 0;
}

function checkIfElement(element: HTMLElement | null): element is HTMLElement {
  return Boolean(element);
}

function checkIfElementVisibleWithinRoot(params: CheckIfElementVisibleWithinRootParams) {
  const { element, root } = params;

  if (!checkIfVisible(element)) return false;

  return checkIfVisibleAncestors({ element, root });
}

function checkIfVisibleAncestors(params: CheckIfElementVisibleWithinRootParams): boolean {
  const { element, root } = params;
  const parentElement = element.parentElement;

  if (!parentElement || parentElement === root) return true;

  if (!checkIfElementDisplayed(parentElement)) return false;

  return checkIfVisibleAncestors({ element: parentElement, root });
}

function restoreClosedConsentSurfaceOnNextFrame() {
  requestAnimationFrame(() => {
    restoreClosedConsentSurface();

    requestAnimationFrame(() => {
      restoreClosedConsentSurface();
    });
  });
}

function restoreClosedConsentSurface() {
  consentSurfaceState.restoreObserver = undefined;
  restoreBackgroundContent();
  restorePreviousFocus();
}

function checkIfVisible(element: HTMLElement) {
  if (!checkIfElementDisplayed(element)) return false;

  const rect = element.getBoundingClientRect();

  return rect.width > 0 && rect.height > 0;
}

function checkIfElementDisplayed(element: HTMLElement) {
  if (element.hidden || element.classList.contains('ot-hide')) return false;

  if (element.getAttribute('aria-hidden') === 'true') return false;

  const style = getComputedStyle(element);

  if (style.display === 'none' || style.visibility === 'hidden') {
    return false;
  }

  return true;
}

function rememberFocusedElement(element: Element | null) {
  if (!(element instanceof HTMLElement)) return;

  const trapTarget = document.querySelector<HTMLElement>(OT_CONSENT_CONTAINER);

  if (trapTarget?.contains(element)) return;

  consentSurfaceState.lastFocusedBeforeConsent = element;
}

function applyBackgroundContentInert() {
  const trapTarget = document.querySelector<HTMLElement>(OT_CONSENT_CONTAINER);

  if (!trapTarget) return;

  const backgroundElements = getBackgroundElements(trapTarget);

  backgroundElements.forEach(applyBackgroundElementInert);
}

function applyBackgroundContentInertForTrapTarget(trapTarget: HTMLElement) {
  if (!checkIfPreferenceCenterTrapTarget(trapTarget)) return;

  applyBackgroundContentInert();
}

function applyBackgroundContentInertForFocusedElement(element: HTMLElement) {
  const preferenceCenter = element.closest<HTMLElement>(PREFERENCE_CENTER_SELECTOR);

  if (!preferenceCenter || !checkIfVisible(preferenceCenter)) {
    restoreClosedConsentSurfaceOnNextFrame();

    return;
  }

  applyBackgroundContentInert();
}

function getBackgroundElements(trapTarget: HTMLElement): HTMLElement[] {
  return getBackgroundElementsFromAncestor({
    currentElement: trapTarget,
    parentElement: trapTarget.parentElement,
  });
}

function getBackgroundElementsFromAncestor(
  params: GetBackgroundElementsFromAncestorParams,
): HTMLElement[] {
  const { currentElement, parentElement } = params;

  if (!parentElement) return [];

  const siblingElements = [...parentElement.children].filter((element): element is HTMLElement =>
    checkIfBackgroundElement({ currentElement, element }),
  );

  if (parentElement === document.body) return siblingElements;

  return [
    ...siblingElements,
    ...getBackgroundElementsFromAncestor({
      currentElement: parentElement,
      parentElement: parentElement.parentElement,
    }),
  ];
}

function applyBackgroundElementInert(element: HTMLElement) {
  rememberBackgroundElementState(element);

  inertBackgroundElements.add(element);

  element.setAttribute('aria-hidden', 'true');
  element.setAttribute('inert', '');
}

function rememberBackgroundElementState(element: HTMLElement) {
  if (backgroundElementStates.has(element)) return;

  backgroundElementStates.set(element, {
    ariaHidden: element.getAttribute('aria-hidden'),
    inert: element.hasAttribute('inert'),
  });
}

function restoreBackgroundContent() {
  inertBackgroundElements.forEach((element) => {
    const state = backgroundElementStates.get(element);

    if (!state) return;

    restoreAriaHidden({ element, value: state.ariaHidden });
    restoreInert({ element, value: state.inert });

    backgroundElementStates.delete(element);
  });

  inertBackgroundElements.clear();
}

function restorePreviousFocus() {
  if (!consentSurfaceState.lastFocusedBeforeConsent?.isConnected) return;

  if (checkIfActiveElementInsideConsentSurface()) return;

  consentSurfaceState.lastFocusedBeforeConsent.focus();
  consentSurfaceState.lastFocusedBeforeConsent = undefined;
}

function checkIfActiveElementInsideConsentSurface() {
  const activeElement = document.activeElement;

  if (!(activeElement instanceof HTMLElement)) return false;

  if (activeElement.closest(BANNER_SELECTOR)) return true;

  const visibleConsentSurface = activeElement.closest<HTMLElement>(PREFERENCE_CENTER_SELECTOR);

  return Boolean(visibleConsentSurface) && checkIfConsentSurfaceVisible(visibleConsentSurface);
}

function restoreAriaHidden(params: RestoreAriaHiddenParams) {
  const { element, value } = params;

  if (value === null) {
    element.removeAttribute('aria-hidden');

    return;
  }

  element.setAttribute('aria-hidden', value);
}

function restoreInert(params: RestoreInertParams) {
  const { element, value } = params;

  if (!value) {
    element.removeAttribute('inert');

    return;
  }

  element.setAttribute('inert', '');
}

function checkIfBackgroundElement(params: CheckIfBackgroundElementParams) {
  const { currentElement, element } = params;

  if (!(element instanceof HTMLElement)) {
    return false;
  }

  if (element === currentElement) {
    return false;
  }

  if (element.tagName === 'SCRIPT' || element.tagName === 'STYLE') {
    return false;
  }

  return true;
}

function checkIfPreferenceCenterTrapTarget(element: HTMLElement) {
  return element.matches(PREFERENCE_CENTER_SELECTOR);
}

function checkIfPreferenceCenterHidden(params: CheckIfPreferenceCenterHiddenParams) {
  const { closeButton, preferenceCenter } = params;

  return (
    preferenceCenter.classList.contains('ot-hide') ||
    (!checkIfVisible(preferenceCenter) && !checkIfVisible(closeButton))
  );
}

function getOneTrust() {
  return (globalThis as GlobalOneTrust).OneTrust;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const OT_CONSENT_CONTAINER = '#onetrust-consent-sdk';
const BANNER_SELECTOR = '#onetrust-banner-sdk';
const PREFERENCE_CENTER_SELECTOR = '#onetrust-pc-sdk';
const PREFERENCE_CENTER_CLOSE_BUTTON_ID = 'close-pc-btn-handler';
const TRAP_APPLIED_ATTRIBUTE = 'data-keyboard-trap-applied';
const PREFERENCE_CENTER_OBSERVER_APPLIED_ATTRIBUTE = 'data-preference-center-observer-applied';
const BANNER_CLOSE_BUTTON_SELECTOR = '#onetrust-banner-sdk .onetrust-close-btn-handler';

const CONSENT_SURFACE_OBSERVER_OPTIONS = {
  attributes: true,
  attributeFilter: ['aria-hidden', 'class', 'hidden', 'style'],
  childList: true,
  subtree: true,
};

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const backgroundElementStates = new WeakMap<HTMLElement, BackgroundElementState>();
const inertBackgroundElements = new Set<HTMLElement>();
const consentSurfaceState: ConsentSurfaceState = { lastFocusedBeforeConsent: undefined };

// ─── Types ───────────────────────────────────────────────────────────────────

type BackgroundElementState = { ariaHidden: string | null; inert: boolean };

type CheckIfBackgroundElementParams = {
  currentElement: HTMLElement;
  element: Element;
};

type CheckIfPreferenceCenterHiddenParams = {
  closeButton: HTMLElement;
  preferenceCenter: HTMLElement;
};

type CheckIfElementVisibleWithinRootParams = {
  element: HTMLElement;
  root: HTMLElement;
};

type FocusEdgeElementParams = {
  event: KeyboardEvent;
  firstFocusable: HTMLElement;
  lastFocusable: HTMLElement;
};

type GetBannerFocusableElementsParams = {
  root: HTMLElement;
  visibleCandidates: HTMLElement[];
};

type HandlePreferenceCenterTabKeyDownParams = {
  activeElement: HTMLElement;
  event: KeyboardEvent;
  firstFocusable: HTMLElement;
  focusableElements: HTMLElement[];
  lastFocusable: HTMLElement;
};

type GetNextFocusableElementParams = {
  activeElement: HTMLElement;
  firstFocusable: HTMLElement;
  focusableElements: HTMLElement[];
  isShiftKeyPressed: boolean;
  lastFocusable: HTMLElement;
};

type GetBackgroundElementsFromAncestorParams = {
  currentElement: HTMLElement;
  parentElement: HTMLElement | null;
};

type RestoreAriaHiddenParams = {
  element: HTMLElement;
  value: string | null;
};

type RestoreIfConsentSurfaceClosedParams = {
  observer: MutationObserver;
};

type RestoreInertParams = {
  element: HTMLElement;
  value: boolean;
};

type SyncBackgroundContentWithPreferenceCenterParams = {
  observer: MutationObserver;
  trapTarget: HTMLElement;
};

type ConsentSurfaceState = {
  hasDocumentListeners?: boolean;
  hasPendingPreferenceCenterSync?: boolean;
  lastFocusedBeforeConsent?: HTMLElement;
  preferenceCenterObserver?: MutationObserver;
  restoreObserver?: MutationObserver;
};

type GlobalOneTrust = {
  OneTrust?: {
    Close?: () => void;
  };
};
